<template>
  <!-- REFACTOR: maybe extend textfield instead of wrapping -->
  <Textfield
    v-bind="$attrs"
    v-on="{ ...$listeners, input: () => {} }"
    v-model="textFieldValue"
    :max="maxValue"
    :min="minValue"
    :step="step"
    :suffix="disableSymbol ? '' : symbol"
    :type="focused ? 'number' : 'text'"
    :hide-spin-buttons="stepButtons"
    :prepend-inner-icon="stepButtons ? 'mdi-minus-circle' : ''"
    :append-icon="stepButtons ? 'mdi-plus-circle' : ''"
    :class="`text-${align}`"
    @focus="onFocus"
    @blur="onBlur"
    @click:prepend-inner="increment(-1 * stepFactor)"
    @click:append="increment(stepFactor)"
  >
    <template #prepend-inner>
      <v-btn
        fab
        width="24"
        height="24"
        elevation="0"
        color="secondary"
        class="input-button"
        :disabled="value <= minValue"
        @click="increment(-1 * stepFactor)"
      >
        <v-icon>mdi-minus</v-icon>
      </v-btn>
    </template>
    <template #append>
      <v-btn
        fab
        width="24"
        height="24"
        elevation="0" 
        color="secondary"
        :disabled="value >= maxValue"
        class="input-button"
        @click="increment(stepFactor)"
      >
        <v-icon>mdi-plus</v-icon>
      </v-btn>
    </template>
    <!-- how to pass/override slots: https://gist.github.com/loilo/73c55ed04917ecf5d682ec70a2a1b8e2 -->
    <!-- pass through scopedSlots with their scope data -->
    <template
      v-for="(_, scopedSlotName) in $scopedSlots"
      #[scopedSlotName]="slotData"
    >
      <span :key="scopedSlotName" class="pr-3">
        <slot :name="scopedSlotName" v-bind="slotData" />
      </span>
    </template>

    <!-- pass through normal slots -->
    <template v-for="(_, slotName) in $slots" #[slotName]>
      <span :key="slotName" class="pr-3">
        <slot :name="slotName" />
      </span>
    </template>
  </Textfield>
</template>

<script>
import Big from 'big.js';

export default {
  props: {
    value: {
      type: [Number, String],
      default: 0,
    },
    unit: {
      type: Object,
      required: false,
    },
    disableSymbol: {
      type: Boolean,
      default: false,
    },
    align: {
      type: String,
      default: 'center',
      validator(v) {
        return ['left', 'center', 'right'].includes(v);
      },
    },
    stepFactor: {
      // larger steps by pressing buttons, but keep original step for rounding and keyboard/mouse controls
      type: [Number, String],
      default: 1,
      validator: v => +v && Number.isSafeInteger(v),
    },
    stepButtons: {
      // show separate buttons for custom increment / stepping, false returns browser-standard spin buttons
      type: Boolean,
      default: true,
    },
    noRounding: {
      type: Boolean,
      default: false,
    },
    format: {
      // Intl.NumberFormat constructor options https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat/NumberFormat
      type: Object,
      default: () => ({}),
      validator(v) {
        try {
          new Intl.NumberFormat('nl', v); // test if options are valid
          return true;
        } catch (err) {
          return false;
        }
      },
    },
  },
  mounted() {
    // TODO: Add initial value for cancel fallback and precision data
    this.realValue = this.normalizeValue(this.value);
    this.inputValue = this.realValue.toString(); // Strip trailing zeroes on init
  },
  computed: {
    maxValue() {
      return this.$attrs['maxValue'] || this.$attrs['max-value'] || this.unit?.maxValue || Number.MAX_VALUE
    },
    minValue() {
      return this.$attrs['minValue'] || this.$attrs['min-value'] || this.unit?.minValue || ((this.maxValue || 0) * -1.0)
    },
    step() {
      return this.$attrs['step'] || this.unit?.step || 1
    },
    symbol() {
      return this.$attrs['symbol'] || this.unit?.symbol || ''
    },
    realMax() {
      return this.isValidNumber(this.maxValue) && new Big(this.maxValue + '');
    },
    realMin() {
      return this.isValidNumber(this.minValue) && new Big(this.minValue + '');
    },
    stepSize() {
      if (this.step && this.step !== 'any') return this.step;

      // TODO: track precision via prop separate from realValue, maybe cache realValue precision onBlur
      const stepSize = new Big(1);
      const bigNum =
        (this.realValue instanceof Big && this.realValue) || stepSize;
      const decimals = bigNum.c.length - (bigNum.e + 1);
      stepSize.e = -1 * decimals;
      return decimals > 0 ? stepSize : 1;
    },
    formatter() {
      return new Intl.NumberFormat(this.$locale, this.format);
    },
    formattedInputValue() {
      return this.formatter.format(this.inputValue);
    },
    textFieldValue: {
      // show formatting when not editing input, only update value through input if being edited directly
      get() {
        return this.focused ? this.inputValue : this.formattedInputValue;
      },
      set(v) {
        if (this.focused) this.inputValue = v;
      },
    },
  },
  data() {
    return {
      realValue: null,
      inputValue: null,
      focused: false,
    };
  },
  watch: {
    value: {
      handler(n) {
        this.inputValue = n.toString();
      },
    },
    inputValue(n) {
      // TODO: decouple from realvalue, to allow formatting in textfield
      if (this.isValidNumber(n)) {
        this.realValue = this.normalizeValue(n);
      }
    },
  },
  methods: {
    clamp() {
      const { realMax, realMin, realValue } = this;

      this.realValue = realValue.gte(realMin)
        ? realValue.lte(realMax)
          ? realValue
          : realMax
        : realMin;
    },
    increment(factor = 1) {
      this.realValue = this.realValue.plus(this.stepSize * factor + '');
      this.updateValue();
    },
    isValidNumber(n) {
      if (typeof n === 'number') return true;
      if (typeof n !== 'string') return false;
      if (n.trim() === '') return false;
      return !Number.isNaN(n);
    },
    normalizeValue(v) {
      if (!this.isValidNumber(v)) v = (this.minValue || 0).toString();
      v = v + '';
      v = v.replace(',', '.'); // decimal point separator
      return new Big(v);
    },
    onFocus() {
      this.focused = true;
    },
    onBlur() {
      this.focused = false;
      this.updateValue();
    },
    roundToStep(fromBase = -1) {
      if (this.step === 'any') return;

      const { realMax, realMin, realValue } = this;
      const step = new Big(this.stepSize);
      const adj = fromBase < 0 ? realMin : fromBase > 0 ? realMax : '0'; // allow adjusting for steps from minimum/maximum bound instead of 0 (default: minimum)

      const remainder = realValue.minus(adj).mod(step);
      this.realValue =
        remainder >= step * 0.5
          ? realValue.plus(step.minus(remainder))
          : realValue.minus(remainder);
    },
    updateValue() {
      // TODO: add precision
      if (!this.noRounding) this.roundToStep();
      this.clamp();

      const result = this.realValue.toString();

      this.$emit('input', result);

      if (this.inputValue === '') {
        // set inputValue if value doesn't change and inputValue is invalid (returns '' default)
        this.inputValue = result;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.v-text-field::v-deep .v-text-field__slot input {
  text-align: inherit;
}
.input-button {
  margin-top: 4px;
  margin-bottom: 4px;
  margin-right: 4px;
}
// .no-spin-buttons::v-deep {
//   input[type='number'] {
//     -moz-appearance: textfield;
//   }

//   input[type='number']::-webkit-inner-spin-button,
//   input[type='number']::-webkit-outer-spin-button {
//     -webkit-appearance: none;
//     margin: 0;
//   }
// }
</style>
<style lang="css" scoped>
/deep/.v-text-field__suffix {
  padding-right: 8px;
}
/deep/.v-input__append-inner {
  margin: 0px !important;
  align-self: center;
}
/deep/.v-input__prepend-inner {
  margin: 0px !important;
  align-self: center;
}
</style>